import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import Navigation from '../components/Navigation';
import { Mail, Lock, AlertCircle, CheckCircle } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const { supabase } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('login');
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    const theme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes('Invalid')) {
          setError('Invalid email or password');
        } else if (error.message.includes('Email not confirmed')) {
          setError('Please confirm your email before logging in');
        } else {
          setError(error.message || 'Login failed. Please try again.');
        }
      } else if (data.user) {
        navigate('/console');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/console`,
        },
      });

      if (error) {
        setError('Google login failed. Please try again.');
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/console`,
      });

      if (error) {
        setError(error.message || 'Could not send reset email. Please try again.');
      } else {
        setResetSent(true);
      }
    } catch (err) {
      console.error('Reset error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root,
        :root[data-theme="light"] {
          --bg: #F4F1EB;
          --bg2: #FDFCF8;
          --bg3: #EAE7DF;
          --ink: #1C1917;
          --ink2: #6B6560;
          --ink3: #A8A19A;
          --fire: #D97642;
          --err: #C0392B;
          --ok: #2D7A4F;
          --line: rgba(28,25,23,0.09);
        }

        :root[data-theme="dark"] {
          --bg: #100E0C;
          --bg2: #1A1714;
          --bg3: #242018;
          --ink: #F0EDE6;
          --ink2: #9E9890;
          --ink3: #665F58;
          --fire: #D97642;
          --err: #E05C4B;
          --ok: #4CAF82;
          --line: rgba(240,237,230,0.08);
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }

        .login-page {
          min-height: 100vh;
          background: var(--bg);
          color: var(--ink);
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .login-body {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem 1.5rem;
        }

        .login-wrap {
          width: 100%;
          max-width: 420px;
        }

        .login-head {
          text-align: center;
          margin-bottom: 2.5rem;
        }

        .login-sub {
          font-size: 0.9rem;
          color: var(--ink2);
        }

        .login-card {
          background: var(--bg2);
          border: 1px solid var(--line);
          border-radius: 0.75rem;
          padding: 2.25rem;
        }

        .error-msg,
        .success-msg {
          display: flex;
          align-items: flex-start;
          gap: 0.65rem;
          padding: 0.875rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          margin-bottom: 1.5rem;
          line-height: 1.5;
        }

        .error-msg {
          background: rgba(192,57,43,0.08);
          border: 1px solid rgba(192,57,43,0.3);
          color: var(--err);
        }

        .success-msg {
          background: rgba(45,122,79,0.08);
          border: 1px solid rgba(45,122,79,0.3);
          color: var(--ok);
        }

        .form-group {
          margin-bottom: 1.25rem;
        }

        .form-label {
          display: block;
          font-size: 0.82rem;
          font-weight: 600;
          font-family: 'Manrope', sans-serif;
          letter-spacing: 0.04em;
          color: var(--ink2);
          margin-bottom: 0.5rem;
          text-transform: uppercase;
        }

        .input-wrap {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--ink3);
          pointer-events: none;
        }

        .form-input {
          width: 100%;
          padding: 0.8rem 0.875rem 0.8rem 2.75rem;
          background: var(--bg3);
          border: 1px solid var(--line);
          border-radius: 0.4rem;
          color: var(--ink);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          transition: border-color 0.15s;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--fire);
        }

        .form-input::placeholder {
          color: var(--ink3);
        }

        .form-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .forgot-link {
          font-size: 0.82rem;
          color: var(--ink3);
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          font-family: 'DM Sans', sans-serif;
          transition: color 0.15s;
        }

        .forgot-link:hover {
          color: var(--fire);
        }

        .btn-primary {
          width: 100%;
          padding: 0.8rem;
          background: var(--ink);
          color: var(--bg);
          border: none;
          border-radius: 0.4rem;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: opacity 0.15s;
          margin-top: 0.25rem;
        }

        .btn-primary:hover {
          opacity: 0.8;
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .divider-line {
          flex: 1;
          height: 1px;
          background: var(--line);
        }

        .divider-text {
          font-size: 0.8rem;
          color: var(--ink3);
        }

        .btn-google {
          width: 100%;
          padding: 0.8rem;
          background: transparent;
          color: var(--ink2);
          border: 1px solid var(--line);
          border-radius: 0.4rem;
          font-family: 'DM Sans', sans-serif;
          font-weight: 500;
          font-size: 0.95rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.65rem;
          transition: border-color 0.15s, color 0.15s;
        }

        .btn-google:hover {
          border-color: var(--ink2);
          color: var(--ink);
        }

        .btn-google:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .back-link {
          margin-top: 1.25rem;
          text-align: center;
          font-size: 0.875rem;
          color: var(--ink2);
        }

        .back-link button {
          color: var(--fire);
          font-weight: 600;
          background: none;
          border: none;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.875rem;
          padding: 0;
        }

        .back-link button:hover {
          text-decoration: underline;
        }

        @media (max-width: 640px) {
          .login-body {
            padding: 2rem 1rem;
          }

          .login-card {
            padding: 1.5rem;
          }

          .login-head {
            margin-bottom: 1.5rem;
          }
        }
      `}</style>

      <Navigation />

      <div className="login-body">
        <div className="login-wrap">
          <div className="login-head">
            <p className="login-sub">
              {view === 'login' ? 'Sign in to your account' : 'Reset your password'}
            </p>
          </div>

          <div className="login-card">
            {view === 'reset' && (
              <>
                {error && (
                  <div className="error-msg">
                    <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span>{error}</span>
                  </div>
                )}

                {resetSent ? (
                  <div className="success-msg">
                    <CheckCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span>Reset link sent. Check your inbox and follow the instructions.</span>
                  </div>
                ) : (
                  <form onSubmit={handleResetPassword}>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <div className="input-wrap">
                        <Mail size={18} className="input-icon" />
                        <input
                          type="email"
                          className="form-input"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          required
                        />
                      </div>
                    </div>

                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? 'Sending…' : 'Send Reset Link'}
                    </button>
                  </form>
                )}

                <div className="back-link">
                  <button
                    onClick={() => {
                      setView('login');
                      setError('');
                      setResetSent(false);
                    }}
                  >
                    Back to sign in
                  </button>
                </div>
              </>
            )}

            {view === 'login' && (
              <>
                {error && (
                  <div className="error-msg">
                    <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleEmailLogin}>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <div className="input-wrap">
                      <Mail size={18} className="input-icon" />
                      <input
                        type="email"
                        className="form-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="form-row">
                      <label className="form-label" style={{ margin: 0 }}>
                        Password
                      </label>
                      <button
                        type="button"
                        className="forgot-link"
                        onClick={() => {
                          setView('reset');
                          setError('');
                        }}
                      >
                        Forgot password?
                      </button>
                    </div>

                    <div className="input-wrap">
                      <Lock size={18} className="input-icon" />
                      <input
                        type="password"
                        className="form-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Signing in…' : 'Sign In'}
                  </button>
                </form>

                <div className="divider">
                  <div className="divider-line" />
                  <span className="divider-text">or</span>
                  <div className="divider-line" />
                </div>

                <button onClick={handleGoogleLogin} className="btn-google" disabled={loading}>
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}